import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ExplainableScoredModule, HomeWorkspace } from '@why-islam/engine';
import { apiJson } from '../api';
import './AdaptiveHomeSection.css';

function IconStar({ className }: { className?: string }) {
  return (
    <svg className={className} width="20" height="20" viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
      />
    </svg>
  );
}

function IconCompass({ className }: { className?: string }) {
  return (
    <svg className={className} width="20" height="20" viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm3.14 4.93l-1.47 5.53 4.4 3.2-5.53 1.47-3.2-4.4-4.4-3.2 5.53-1.47 3.2 4.4z"
      />
    </svg>
  );
}

function IconList({ className }: { className?: string }) {
  return (
    <svg className={className} width="18" height="18" viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M4 6h2v2H4V6zm0 5h2v2H4v-2zm0 5h2v2H4v-2zM9 6h11v2H9V6zm0 5h11v2H9v-2zm0 5h11v2H9v-2z"
      />
    </svg>
  );
}

async function trackRecClick(moduleId: string, slug: string) {
  try {
    await apiJson('/api/events', {
      method: 'POST',
      body: JSON.stringify({
        eventType: 'recommendation_clicked',
        entityType: 'module',
        entityId: moduleId,
        payload: { slug, surface: 'home_adaptive' },
      }),
    });
  } catch {
    /* ignore */
  }
}

function RecLink(props: { item: ExplainableScoredModule; hint?: string }) {
  const { item, hint } = props;
  const sub = hint ?? item.explanations[0]?.human ?? '';
  return (
    <li key={item.module.slug}>
      <Link
        to={`/m/${item.module.slug}`}
        onClick={() => trackRecClick(item.module.id, item.module.slug)}
      >
        {item.module.title}
      </Link>
      {sub ? <span className="wi-adaptive-mini">{sub}</span> : null}
    </li>
  );
}

export function AdaptiveHomeSection() {
  const [ws, setWs] = useState<HomeWorkspace | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await apiJson<{ workspace: HomeWorkspace }>('/api/home');
        if (!cancelled) setWs(r.workspace);
      } catch {
        if (!cancelled) setWs(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let raf = 0;
    if (ws) raf = window.requestAnimationFrame(() => setMounted(true));
    return () => window.cancelAnimationFrame(raf);
  }, [ws]);

  if (!ws) return null;

  const primary = ws.primaryNext;
  const cont = ws.continueWhereLeftOff?.[0];
  const paused = ws.pausedTopics?.slice(0, 3) ?? [];
  const difficult = ws.difficultCareful?.slice(0, 3) ?? [];

  return (
    <section className={`wi-adaptive-band wi-adaptive-band--premium${mounted ? ' is-in-view' : ''}`} aria-label="Suggested for you">
      <div className="wi-section-inner">
        <div className="wi-adaptive-head wi-adaptive-head--premium">
          <p className="wi-kicker">Shaped to you</p>
          <h2 className="section-title">Continue your inquiry</h2>
          <p className="muted section-sub">
            Recommendations use your profile and recent exploration—each suggestion includes a brief reason.
          </p>
        </div>
        <div className="wi-adaptive-grid wi-adaptive-grid--premium">
          {primary ? (
            <article className="wi-adaptive-card wi-adaptive-card--premium wi-adaptive-card--primary">
              <div className="wi-adaptive-card__top">
                <p className="wi-adaptive-label">Start here</p>
                <IconStar className="wi-adaptive-icon" />
              </div>
              <h3>
                <Link
                  to={`/m/${primary.module.slug}`}
                  onClick={() => trackRecClick(primary.module.id, primary.module.slug)}
                >
                  {primary.module.title}
                </Link>
              </h3>
              {primary.module.summary ? <p>{primary.module.summary}</p> : null}
              <p className="wi-adaptive-why">
                <span>Why:</span> {primary.explanations.map((e) => e.human).join(' ') || ws.primaryExplanation}
              </p>
            </article>
          ) : null}

          {cont ? (
            <article className="wi-adaptive-card wi-adaptive-card--premium wi-adaptive-card--spotlight">
              <div className="wi-adaptive-card__top">
                <p className="wi-adaptive-label">Because you explored</p>
                <IconCompass className="wi-adaptive-icon" />
              </div>
              <h3>
                <Link to={`/m/${cont.module.slug}`} onClick={() => trackRecClick(cont.module.id, cont.module.slug)}>
                  {cont.module.title}
                </Link>
              </h3>
              <p className="wi-adaptive-why">
                <span>Why:</span> {cont.explanations[0]?.human ?? 'Picks up near where you left off.'}
              </p>
              <p className="wi-adaptive-journey-hint">
                Prefer structure? <Link to="/journeys">Browse guided journeys</Link>.
              </p>
            </article>
          ) : null}

          <div className="wi-adaptive-list-shell">
            <p className="wi-adaptive-label">Questions close to yours</p>
            <ul className="wi-adaptive-list-premium-ul">
              {ws.questionsCloseToYours.slice(0, 4).map((x) => (
                <RecLink key={x.module.slug} item={x} />
              ))}
            </ul>
          </div>

          <div className="wi-adaptive-list-shell">
            <p className="wi-adaptive-label wi-adaptive-label--row">
              <IconList className="wi-adaptive-label-icon" />
              <span>Foundations</span>
            </p>
            <ul className="wi-adaptive-list-premium-ul">
              {ws.foundations.slice(0, 4).map((x) => (
                <RecLink key={x.module.slug} item={x} />
              ))}
            </ul>
          </div>

          {paused.length ? (
            <div className="wi-adaptive-list-shell">
              <p className="wi-adaptive-label">Clarify what felt confusing</p>
              <ul className="wi-adaptive-list-premium-ul">
                {paused.map((x) => (
                  <RecLink
                    key={x.module.slug}
                    item={x}
                    hint="You marked something nearby as confusing—try a gentler angle here."
                  />
                ))}
              </ul>
            </div>
          ) : null}

          {difficult.length ? (
            <div className="wi-adaptive-list-shell wi-adaptive-list--difficult">
              <p className="wi-adaptive-label">Difficult topics (careful pacing)</p>
              <ul className="wi-adaptive-list-premium-ul">
                {difficult.map((x) => (
                  <RecLink
                    key={x.module.slug}
                    item={x}
                    hint="High sensitivity—read slowly, with context and patience."
                  />
                ))}
              </ul>
            </div>
          ) : null}
        </div>
        <p className="wi-adaptive-foot wi-adaptive-foot--premium">
          <Link to="/study">Adjust how recommendations work</Link>
        </p>
      </div>
    </section>
  );
}
